import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../auth/AuthProvider';
import { submitWaitlistEmail } from '../../data/cloud';
import { isProductSurfaceAllowed } from '../marketing/funnel/marketingConfig';
import { usePrerenderSignal } from '../../utils/usePrerenderSignal';
import { trackEvent } from '../../utils/analytics';
import { GuideScreen } from './GuideScreen';
import { GoalAccordion } from './GoalAccordion';
import { Citations } from '../../components/Citations';
import { PRIMER, GOALS, REASSURANCE, SAFETY } from './peptides101.content';
import { loadLearnProgress, saveLearnProgress, clearLearnProgress } from './learnProgress';
import { PRIMER_ILLUSTRATIONS } from './illustrations101';
import './Peptides101.css';

type Phase = 'primer' | 'chooser' | 'goal' | 'reassurance' | 'safety' | 'next';

// Restore a saved position, but validate it against the current content so a
// stale goalId or out-of-range primer index can never wedge the guide.
function computeInitialState(): { phase: Phase; primerIndex: number; goalId: string | null } {
  const saved = loadLearnProgress();
  if (!saved) return { phase: 'primer', primerIndex: 0, goalId: null };
  const primerIndex = Math.min(Math.max(0, saved.primerIndex), PRIMER.length - 1);
  // Every post-chooser phase needs a valid goal to render; otherwise send the
  // visitor back to the chooser rather than wedge on a stale id.
  if (saved.phase === 'goal' || saved.phase === 'reassurance' || saved.phase === 'safety' || saved.phase === 'next') {
    const valid = saved.goalId != null && GOALS.some((g) => g.id === saved.goalId);
    return valid
      ? { phase: saved.phase, primerIndex, goalId: saved.goalId }
      : { phase: 'chooser', primerIndex, goalId: null };
  }
  return { phase: saved.phase, primerIndex, goalId: saved.goalId };
}

const Peptides101: React.FC = () => {
  const navigate = useNavigate();
  const { session } = useAuth();
  const [initial] = useState(computeInitialState);
  const [phase, setPhase] = useState<Phase>(initial.phase);
  const [primerIndex, setPrimerIndex] = useState(initial.primerIndex);
  const [goalId, setGoalId] = useState<string | null>(initial.goalId);

  // Logged-out completion email capture (finding: /learn completion sent
  // signed-out visitors to / with no soft-conversion path). Wires to the
  // same submitWaitlistEmail() the funnel uses, tagged source 'learn-complete'.
  const [email, setEmail] = useState('');
  const [emailStatus, setEmailStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [emailError, setEmailError] = useState('');

  const onLearnEmailSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed || emailStatus === 'submitting' || emailStatus === 'success') return;
    // Validate here (form is noValidate to keep the styled error path).
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(trimmed)) {
      setEmailStatus('error');
      setEmailError('That email does not look right. Check it and try again.');
      return;
    }
    setEmailStatus('submitting');
    setEmailError('');
    const result = await submitWaitlistEmail(trimmed, 'learn-complete');
    if (result.success) {
      setEmailStatus('success');
    } else {
      setEmailStatus('error');
      setEmailError(result.error || 'Something went wrong. Please try again.');
    }
  }, [email, emailStatus]);

  usePrerenderSignal(true);
  useEffect(() => {
    // Skip the mount event during the Puppeteer prerender (and for bots),
    // which set navigator.webdriver. Otherwise every deploy inserts a
    // spurious learn_guide_start row that deflates the guide's funnel.
    if (typeof navigator !== 'undefined' && navigator.webdriver) return;
    void trackEvent('learn_guide_start');
  }, []);

  // Persist the visitor's place so they can leave and resume.
  useEffect(() => {
    saveLearnProgress({ phase, primerIndex, goalId });
  }, [phase, primerIndex, goalId]);

  // WEB LOCKDOWN (marketingConfig switch): on web there is no app to enter,
  // so /learn behaves as a pure lead-gen surface - no sign-in affordance, no
  // in-app "doors". Only native (or a restored web-auth switch) gets those.
  const appAccess = session && isProductSurfaceAllowed();

  const close = () => navigate(appAccess ? '/resources' : '/');

  // Logged-out visitors (shared link) get a brand + sign-in row locked into
  // the sticky header. Authed native users get no top chrome. On web the
  // sign-in link is removed entirely - only the wordmark remains.
  const brandRow = !appAccess ? (
    <>
      <Link to="/" className="lp-brandlink">PRTCL<span className="lp-brandlink-dot">.</span></Link>
      {isProductSurfaceAllowed() && <Link to="/signin" className="lp-signin">Sign in</Link>}
    </>
  ) : undefined;

  let body: React.ReactNode;

  if (phase === 'primer') {
    const card = PRIMER[primerIndex];
    const isLast = primerIndex === PRIMER.length - 1;
    const Illus = PRIMER_ILLUSTRATIONS[card.id];
    body = (
      <GuideScreen
        eyebrow={card.eyebrow}
        question={card.question}
        brandRow={brandRow}
        onBack={primerIndex > 0 ? () => setPrimerIndex((i) => i - 1) : undefined}
        onClose={close}
        progress={{ index: primerIndex, total: PRIMER.length }}
        next={{
          label: isLast ? 'Find my goal' : 'Got it',
          onNext: () => {
            if (isLast) { void trackEvent('learn_primer_complete'); setPhase('chooser'); }
            else setPrimerIndex((i) => i + 1);
          },
        }}
      >
        <div className="lp-card">
          {Illus && <Illus />}
          {card.body.map((para, i) => <p key={i}>{para}</p>)}
        </div>
      </GuideScreen>
    );
  } else if (phase === 'chooser') {
    body = (
      <GuideScreen
        eyebrow="PEPTIDES 101"
        question="Why do you want to take a peptide?"
        lede="Tap what matters most. You can always come back and pick another."
        brandRow={brandRow}
        onBack={() => { setPhase('primer'); setPrimerIndex(PRIMER.length - 1); }}
        onClose={close}
      >
        <div className="lp-goal-grid">
          {GOALS.map((g) => (
            <button
              key={g.id}
              className="lp-goal-card"
              onClick={() => {
                setGoalId(g.id);
                setPhase('goal');
                void trackEvent('learn_goal_selected', { goal: g.id });
              }}
            >
              <span className="lp-goal-label">{g.label}</span>
            </button>
          ))}
        </div>
      </GuideScreen>
    );
  } else if (phase === 'goal') {
    const goal = GOALS.find((g) => g.id === goalId) ?? GOALS[0];
    body = (
      <GuideScreen
        eyebrow="FOR YOUR GOAL"
        question={goal.label}
        brandRow={brandRow}
        onBack={() => setPhase('chooser')}
        onClose={close}
        next={{
          label: 'Continue',
          onNext: () => { void trackEvent('learn_goal_continue', { goal: goal.id }); setPhase('reassurance'); },
        }}
      >
        <GoalAccordion goal={goal} />
      </GuideScreen>
    );
  } else if (phase === 'reassurance') {
    body = (
      <GuideScreen
        eyebrow={REASSURANCE.eyebrow}
        question={REASSURANCE.heading}
        brandRow={brandRow}
        onBack={() => setPhase('goal')}
        onClose={close}
        next={{ label: 'Makes sense', onNext: () => setPhase('safety') }}
      >
        <div className="lp-card lp-safety">
          <p>{REASSURANCE.intro}</p>
          <ul className="lp-safety-list">
            {REASSURANCE.items.map((it, i) => (
              <li key={i}>
                <strong>{it.lead}</strong> {it.rest}
                <Citations sources={it.sources} variant="inline" />
              </li>
            ))}
          </ul>
          <p>{REASSURANCE.closer}</p>
        </div>
      </GuideScreen>
    );
  } else if (phase === 'safety') {
    body = (
      <GuideScreen
        eyebrow={SAFETY.eyebrow}
        question={SAFETY.heading}
        brandRow={brandRow}
        onBack={() => setPhase('reassurance')}
        onClose={close}
        next={{ label: 'Continue', onNext: () => setPhase('next') }}
      >
        <div className="lp-card lp-safety">
          <p>{SAFETY.intro}</p>
          <ul className="lp-safety-list">
            {SAFETY.items.map((it, i) => (
              <li key={i}>
                <strong>{it.lead}</strong> {it.rest}
                <Citations sources={it.sources} variant="inline" />
              </li>
            ))}
          </ul>
          <p>{SAFETY.closer}</p>
          {/* App Review 1.4.1: the source index is one tap from the guide that
              a new user is most likely to read first. */}
          <Link className="lp-acc-link" to="/references">
            See all sources PRTCL cites
          </Link>
          <p className="lp-learn-disclaimer">
            PRTCL does not provide medical advice, diagnosis, or treatment. Information on
            this page and in the app is for educational purposes only. Always consult your
            healthcare provider before starting any peptide protocol.
          </p>
        </div>
      </GuideScreen>
    );
  } else {
    const goal = GOALS.find((g) => g.id === goalId) ?? GOALS[0];
    body = (
      <GuideScreen
        eyebrow="ALMOST THERE"
        question="What do you want to do next?"
        brandRow={brandRow}
        onBack={() => setPhase('goal')}
        onClose={close}
      >
        {appAccess ? (
          <div className="lp-doors">
            <button
              className="lp-door"
              onClick={() => { void trackEvent('learn_door_click', { goal: goal.id, dest: 'guides' }); clearLearnProgress(); navigate('/resources'); }}
            >
              Explore more helpful guides
            </button>
            <button
              className="lp-door"
              onClick={() => { void trackEvent('learn_door_click', { goal: goal.id, dest: 'dashboard' }); clearLearnProgress(); navigate('/dashboard'); }}
            >
              Go to my dashboard
            </button>
            <button
              className="lp-door"
              onClick={() => { void trackEvent('learn_door_click', { goal: goal.id, dest: 'reconstitute' }); clearLearnProgress(); navigate('/beta-onboarding?mode=in-app'); }}
            >
              Reconstitute my first peptide
            </button>
          </div>
        ) : emailStatus === 'success' ? (
          <div className="lp-card lp-learn-capture-done" role="status">
            <p className="lp-learn-capture-title">You're on the list.</p>
            <p>
              Your beginner cheat sheet is ready:{' '}
              <a href="/downloads/before-your-first-peptide.pdf" download>download it here</a>.
              We will also send short notes and new guides as we publish them. The PRTCL app
              itself is on the App Store now, free to download.
            </p>
            <button
              type="button"
              className="lp-door lp-door-primary"
              onClick={() => { void trackEvent('learn_door_click', { goal: goal.id, dest: 'signup' }); clearLearnProgress(); navigate('/'); }}
            >
              Explore PRTCL
            </button>
          </div>
        ) : (
          <div className="lp-card lp-learn-capture">
            <p className="lp-learn-capture-title">Get the beginner cheat sheet</p>
            <p className="lp-learn-capture-lede">A one-page primer on your first peptide, plus short notes and new guides as we publish them. No spam. The PRTCL app is on the App Store now, free to download.</p>
            <form className="lp-learn-capture-form" onSubmit={onLearnEmailSubmit} noValidate>
              <input
                type="email"
                required
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={emailStatus === 'submitting'}
                className="lp-learn-capture-input"
                aria-label="Email address"
              />
              <button type="submit" className="lp-cta" disabled={!email || emailStatus === 'submitting'}>
                {emailStatus === 'submitting' ? 'Sending...' : 'Send me the cheat sheet'}
              </button>
            </form>
            {emailStatus === 'error' && <p className="lp-learn-capture-error" role="alert">{emailError}</p>}
            <button
              type="button"
              className="lp-secondary"
              onClick={() => { void trackEvent('learn_door_click', { goal: goal.id, dest: 'signup' }); clearLearnProgress(); navigate('/'); }}
            >
              No thanks, take me to PRTCL
            </button>
          </div>
        )}
        <p className="lp-learn-disclaimer">
          PRTCL does not provide medical advice, diagnosis, or treatment. Information on
          this page and in the app is for educational purposes only. Always consult your
          healthcare provider before starting any peptide protocol.
        </p>
      </GuideScreen>
    );
  }

  return (
    <>
      <Helmet>
        <title>Peptides 101: A Beginner's Guide | PRTCL</title>
        <meta name="description" content="New to peptides? Learn what they are, how they work, and which ones fit your goal." />
        <link rel="canonical" href="https://www.prtclapp.com/learn" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://www.prtclapp.com/learn" />
        <meta property="og:title" content="Peptides 101: A Beginner's Guide" />
        <meta property="og:description" content="Learn what peptides are and which ones fit your goal." />
        <meta property="og:image" content="https://www.prtclapp.com/images/og-landing-v2.png" />
        <meta property="og:site_name" content="PRTCL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Peptides 101: A Beginner's Guide" />
        <meta name="twitter:description" content="Learn what peptides are and which ones fit your goal." />
        <meta name="twitter:image" content="https://www.prtclapp.com/images/og-landing-v2.png" />
      </Helmet>
      {body}
    </>
  );
};

export default Peptides101;
